// =============================================================================
// nexusApi — authenticated fetch helper for the nexus-core REST surface.
//
// This is the single choke-point every super-admin approval/decision action
// MUST go through. It carries the Supabase user JWT as a bearer so requests hit
// the SERVICE-ROLE-backed nexus-core routes (which perform the real state
// transition + emit NATS events), instead of the client writing straight to
// Postgres — the bug that made proposal "approve" a status-flip that bypassed
// ProposalWorkflowEngine entirely (see docs/02-ARCHITECTURE/SUPERADMIN_NERVOUS_UPGRADE.md §G.1).
//
// Base URL: VITE_NEXUS_API_URL (Vercel rewrite `/nexus-api/*` → nexus-core).
// Paths passed in start at `/api/nexus/...`.
// =============================================================================

// Pure URL/error helpers live in nexusApiCore (no Supabase import) so they are
// unit-testable without Supabase env at import time. Re-exported below so
// existing callers importing them from `./nexusApi` keep working.
import { poOdmowie401 } from './martwaSesja';
import {
  NexusApiError,
  resolveNexusUrl,
  nexusErrorMessage,
  nexusErrorEnvelope,
} from './nexusApiCore';
import { supabase } from './supabase';

export {
  NexusApiError,
  resolveNexusUrl,
  nexusErrorMessage,
  nexusErrorEnvelope,
} from './nexusApiCore';

const DEFAULT_TIMEOUT_MS = 60_000;

/** Jedno zdanie po zakończeniu martwej sesji — ekran logowania mówi resztę. */
export const ZDANIE_MARTWEJ_SESJI = 'Sesja została zakończona — zaloguj się ponownie.';

async function authHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  opts?: { timeoutMs?: number; signal?: AbortSignal }
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  // Chain an external abort signal into ours if provided.
  if (opts?.signal) {
    if (opts.signal.aborted) controller.abort();
    else opts.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(await authHeaders()),
    };
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    const res = await fetch(resolveNexusUrl(path), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      // 401 z nexus-core to pytanie o sesję, nie komunikat: token z pamięci
      // mógł przestać istnieć po stronie serwera auth (lib/martwaSesja.ts).
      if (res.status === 401 && (await poOdmowie401('nexus-core'))) {
        throw new NexusApiError(401, ZDANIE_MARTWEJ_SESJI);
      }
      throw new NexusApiError(res.status, nexusErrorMessage(res.status, text));
    }
    // 204 / empty body → return undefined as T.
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const parsed = text ? JSON.parse(text) : undefined;
    // Some nexus-core routes return `{ error: '...' }` WITH a 2xx status
    // (e.g. innovation approve/reject). Treat that as a failure so callers
    // (approval boards) surface it instead of resolving as success.
    const envelopeError = nexusErrorEnvelope(parsed);
    if (envelopeError) throw new NexusApiError(res.status, envelopeError);
    return parsed as T;
  } catch (err) {
    if (err instanceof NexusApiError) throw err;
    if ((err as Error).name === 'AbortError') {
      throw new NexusApiError(0, 'Przekroczono czas oczekiwania na odpowiedź NEXUS.');
    }
    throw new NexusApiError(0, (err as Error).message || 'Błąd sieci.');
  } finally {
    clearTimeout(timeout);
  }
}

export const nexusGet = <T = unknown>(
  path: string,
  opts?: { timeoutMs?: number; signal?: AbortSignal }
) => request<T>('GET', path, undefined, opts);

export const nexusPost = <T = unknown>(
  path: string,
  body?: unknown,
  opts?: { timeoutMs?: number; signal?: AbortSignal }
) => request<T>('POST', path, body, opts);

export const nexusPut = <T = unknown>(
  path: string,
  body?: unknown,
  opts?: { timeoutMs?: number; signal?: AbortSignal }
) => request<T>('PUT', path, body, opts);

export const nexusPatch = <T = unknown>(
  path: string,
  body?: unknown,
  opts?: { timeoutMs?: number; signal?: AbortSignal }
) => request<T>('PATCH', path, body, opts);

export const nexusDelete = <T = unknown>(
  path: string,
  opts?: { timeoutMs?: number; signal?: AbortSignal }
) => request<T>('DELETE', path, undefined, opts);
